/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
 */
import { getKeyboardFocusableElements } from '../dom/dom.js';

/**
 * Svelte Action to create a focus trap
 *
 * IMPORTANT: when creating a focus trap, always provide a way to break out!
 * For example, a button within the trap which destroys the container,
 * or sends focus to an outer element.
 *
 * @example
 *   <div use:focusTrap>
 *     <button>Ok</button>
 *     <button>Cancel</button>
 *   </div>
 *
 * @param {HTMLElement} node - the DOM node to which the action is applied
 * @param {Object} [params] - parameters for the action
 * @param {boolean} [params.enabled=true] - whether the focus trap is currently active
 * @returns {Object} - node's lifecycle hooks
 */
export default function focusTrap(node, params) {
    let enabled = params?.enabled ?? true; // Default to true if not provided
    let hasFocus = false;

    /**
     * Runs when the rendered node receives key input
     * If focus is within the node, it will be trapped
     * @param {HTMLEvent} event - the handler event
     */
    function handleKeyDown(event) {
        if (enabled && hasFocus && event.key === 'Tab') {
            // trap focus
            const tabbableNodes = getKeyboardFocusableElements(node);

            if (tabbableNodes.length) {
                let index = tabbableNodes.indexOf(document.activeElement);
                if (index === -1 && event.shiftKey) {
                    index = 0;
                }
                index += tabbableNodes.length + (event.shiftKey ? -1 : 1);
                index %= tabbableNodes.length;

                tabbableNodes[index].focus();
                event.preventDefault();
            }
        }
    }

    /**
     * Runs when node (or its contents) receives focus
     */
    function handleFocusIn() {
        if (enabled) {
            hasFocus = true;
            window.addEventListener('keydown', handleKeyDown);
        }
    }

    /**
     * Runs when node (or its contents) loses focus
     */
    function handleFocusOut() {
        // hasFocus is checked to ensure we only remove the listener if it was added.
        // This prevents issues if the trap is disabled while not focused.
        if (hasFocus) {
            window.removeEventListener('keydown', handleKeyDown);
        }
        hasFocus = false;
    }

    // Initialise the trap
    node.addEventListener('focusin', handleFocusIn);
    node.addEventListener('focusout', handleFocusOut);

    return {
        /**
         * Hook on component lifecycle: update parameters
         * @param {Object} newParams - new parameters for the action
         * @param {boolean} [newParams.enabled=true] - whether the focus trap is currently active
         */
        update(newParams) {
            enabled = newParams?.enabled ?? true;
            // If the trap is being disabled while focus is inside, remove the keydown listener.
            if (!enabled && hasFocus) {
                window.removeEventListener('keydown', handleKeyDown);
            } else if (enabled && node.contains(document.activeElement) && !hasFocus) {
                // If the trap is re-enabled while focus is already inside, re-add the listener.
                hasFocus = true;
                window.addEventListener('keydown', handleKeyDown);
            }
        },
        /**
         * Hook on component lifecycle: remove global and local listeners
         */
        destroy() {
            node.removeEventListener('focusin', handleFocusIn);
            node.removeEventListener('focusout', handleFocusOut);
            window.removeEventListener('keydown', handleKeyDown);
        }
    };
}
